import React, { useCallback, useState } from 'react';
import AccessibleWorkflowDiagram from '@jira/project-settings__accessible-workflow-diagram/src/WrappedDiagram.tsx';
import getWorkflow from '@jira/project-settings__project-configuration-commons/src/services/get-workflow/index.tsx';
import { getReadViewWorkflow } from '@jira/project-settings__project-configuration-commons/src/services/get-read-view-workflow/index.tsx';
import type {
	Status,
	MetaWorkflowDocument,
} from '@jira/project-settings__project-configuration-commons/src/index.tsx';
import { ModalBody, ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import AkSpinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useIntl, defineMessages } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import errorImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useEffectWithoutConcurrency } from '@atlassian/jira-react-strict-mode/src/useEffectWithoutConcurrency.tsx';
import { ZoomControls } from './ZoomControls.tsx';

const messages = defineMessages({
	closeButton: {
		id: 'workflow-view-diagram-modal.close-button',
		defaultMessage: 'Close',
		description: 'Label for the button to close the workflow view diagram modal',
	},

	errorMessage: {
		defaultMessage: 'We couldn’t load the workflow.',
		id: 'workflow-view-diagram-modal.error-message',
		description: 'Error message for when the modal fails to load its content',
	},

	noPermissionToViewThisWorkflow: {
		defaultMessage: 'You do not have permission to view this workflow.',
		id: 'workflow-view-diagram-modal.no-permission-to-view-this-workflow',
		description: 'Error message for when a user does not have permission to view a workflow',
	},
});

type WorkflowIdentifier = {
	workflowId?: string;
	workflowName?: string;
};

export type Props = {
	projectId?: string;
	workflowIdentifier: WorkflowIdentifier;
	onClose: () => void;
};

interface WorkflowState {
	data: MetaWorkflowDocument | undefined;
	isLoading: boolean;
	hasError: boolean;
	customErrorMessage?: string;
}

const INITIAL_ZOOM = 1;
const ERROR_IMAGE_MAX_HEIGHT = 212;
const ERROR_LOGGER_PATTERN = '#workflow-view-diagram-modal#';

// Custom hook for workflow data management
const useWorkflowData = (
	cloudId: string,
	workflowIdentifier: WorkflowIdentifier,
	projectId?: string,
) => {
	const [state, setState] = useState<WorkflowState>({
		data: undefined,
		isLoading: true,
		hasError: false,
		customErrorMessage: '',
	});
	const isAdmin = useIsAdmin();
	const { formatMessage } = useIntl();

	useEffectWithoutConcurrency(() => {
		const fetchWorkflow = async () => {
			setState((prev) => ({ ...prev, isLoading: true, hasError: false }));

			try {
				if (isAdmin) {
					const workflowResponse = await getWorkflow(cloudId, workflowIdentifier);
					setState({
						data: workflowResponse,
						isLoading: false,
						hasError: false,
					});
					return;
				}

				if (!projectId) {
					fireErrorAnalytics({
						meta: {
							id: `${ERROR_LOGGER_PATTERN}.missing.project.id`,
							packageName: 'jiraWorkflowViewDiagramModal',
							teamName: 'helix',
						},

						error: new Error('Missing projectId for non-admin user'),
					});
					setState((prev) => ({
						...prev,
						hasError: true,
						isLoading: false,
						customErrorMessage: formatMessage(messages.noPermissionToViewThisWorkflow),
					}));
					return;
				}

				const workflowResponse = await getReadViewWorkflow({
					cloudId,
					projectId,
					...workflowIdentifier,
				});
				setState({
					data: workflowResponse,
					isLoading: false,
					hasError: false,
				});
			} catch (error: unknown) {
				if (error instanceof FetchError && error.statusCode) {
					log.safeErrorWithoutCustomerData(
						`${ERROR_LOGGER_PATTERN}.fetch.failure.${error.statusCode}`,
						error.message || 'Fetch failure with empty error message',
						error,
					);
				} else if (error instanceof TypeError) {
					log.safeErrorWithoutCustomerData(
						`${ERROR_LOGGER_PATTERN}.fetch.failure`,
						error.message,
						error,
					);
				}
				fireErrorAnalytics({
					meta: {
						id: `${ERROR_LOGGER_PATTERN}.fetch.error`,
						packageName: 'jiraWorkflowViewDiagramModal',
						teamName: 'helix',
					},
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
				});
				setState((prev) => ({ ...prev, hasError: true, isLoading: false }));
			}
		};

		fetchWorkflow();
	}, [cloudId, workflowIdentifier]);

	return state;
};

// Workflow Diagram Content Component
const WorkflowDiagramContent = ({
	workflow,
	zoom,
	onZoomChange,
	onZoomUpdate,
}: {
	workflow: MetaWorkflowDocument;
	zoom: number;
	onZoomChange: (zoom: number) => void;
	onZoomUpdate: (zoom: number) => void;
}) => {
	const statuses = workflow.visibleStatuses.map<Status>(
		({ ref: statusId }) => workflow.allStatuses[statusId],
	);

	return (
		<Box xcss={wrapperStyles}>
			<Box xcss={diagramContentWrapperStyles}>
				<AccessibleWorkflowDiagram
					startNodeLayout={workflow.startNodeLayout}
					statuses={statuses}
					transitions={Object.values(workflow.transitions)}
					zoom={zoom}
					isImmutable
					onZoomUpdate={onZoomUpdate}
					collapseTransitionLabels
					shouldShowGlobalLoops
				/>
			</Box>
			<ZoomControls zoom={zoom} onZoomChange={onZoomChange} />
		</Box>
	);
};

// Main Component
export const WorkflowDiagramViewModal = ({ projectId, workflowIdentifier, onClose }: Props) => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const [zoom, setZoom] = useState(INITIAL_ZOOM);

	const {
		data: workflow,
		isLoading,
		hasError,
		customErrorMessage,
	} = useWorkflowData(cloudId, workflowIdentifier, projectId);

	const onZoomChange = useCallback((newZoomValue: number) => {
		setZoom(newZoomValue);
	}, []);

	return (
		<>
			<ModalDialog
				messageId="workflow-view-diagram-modal.modal-dialog.workflow-diagram-view-modal"
				messageType="transactional"
				shouldCloseOnEscapePress
				shouldCloseOnOverlayClick
				onClose={onClose}
				width="100%"
			>
				<Box xcss={modalContentWrapperStyles}>
					<ModalHeader hasCloseButton>
						{workflow && !isLoading && (
							<ModalTitle testId="workflow-view-diagram-modal.modal-title">
								{workflow.name}
							</ModalTitle>
						)}
					</ModalHeader>
					<ModalBody>
						<Box xcss={contentStyles}>
							{(() => {
								if (!hasError && isLoading && !workflow) {
									return (
										// Loading state
										<Box xcss={centerWrapperStyles} testId="workflow-view-diagram-modal.loading">
											<AkSpinner size="large" />
										</Box>
									);
								}
								if ((hasError && !isLoading) || !workflow) {
									return (
										// Error state
										<EmptyState
											header={formatMessage(messages.errorMessage)}
											maxImageHeight={ERROR_IMAGE_MAX_HEIGHT}
											renderImage={getAkEmptyStateRenderImageFn(errorImage)}
											description={customErrorMessage || undefined}
										/>
									);
								}
								return (
									<WorkflowDiagramContent
										workflow={workflow}
										zoom={zoom}
										onZoomChange={onZoomChange}
										onZoomUpdate={setZoom}
									/>
								);
							})()}
						</Box>
					</ModalBody>
					<ModalFooter>
						<Button onClick={onClose} appearance="subtle">
							{formatMessage(messages.closeButton)}
						</Button>
					</ModalFooter>
				</Box>
			</ModalDialog>
			<FireScreenAnalytics />
		</>
	);
};

const centerWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexGrow: 1,
	height: '100%',
});

const wrapperStyles = xcss({
	display: 'flex',
	flexGrow: '1',
	minHeight: '352px',
	boxShadow: 'elevation.shadow.raised',
});

const diagramContentWrapperStyles = xcss({
	width: '100%',
	height: 'auto',
});

const contentStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	flexDirection: 'column',
	height: '100%',
});

const getmodalcontentwrapperheight = () =>
	`calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${PAGE_LAYOUT_OFFSET_TOP})`;

const modalContentWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	height: getmodalcontentwrapperheight(),
	display: 'flex',
	flexGrow: 1,
	flexDirection: 'column',
	justifyContent: 'space-between',
	borderRadius: 'radius.small',
});
